import React, { useState, useEffect, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMetaAds } from 'hooks/useMetaAds';
import GenericModal, { NON_DISMISSIBLE_MODAL_PROPS } from './GenericModal';
import { MetaIcon } from 'components';
import { toast } from 'react-hot-toast';
import MetaDropdowns from '../adsConnections/account/MetaDropdowns';
import MetaPermissionInlineNotice from '../adsConnections/account/MetaPermissionInlineNotice';
import { ModalsContext } from '../context/ModalsContext';
import { AuthContext } from '../context/AuthContext';
import { objetivosQueryKeys, fetchObjetivosForBrand } from 'queries/objetivos';
import { hasPlatformObjetivos } from 'utils/objetivosUtils';

/**
 * ConnectMetaAdsAccountModal Component
 * 
 * Modal para seleccionar una cuenta de Meta Ads después de conectar con Meta.
 * Permite al usuario elegir entre las cuentas de Meta Ads disponibles.
 * Nota: en este flujo solo se selecciona la cuenta publicitaria.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 */
const ConnectMetaAdsAccountModal = ({ isOpen, onClose }) => {
  const { openCreateObjectivesSetupModal } = useContext(ModalsContext);
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const {
    accounts,
    fetchMetaAccounts,
    updateMetaConnectionData,
    loadingAccounts,
    metaAccountsPermissionError,
    startMetaOAuth,
  } = useMetaAds();

  const [selectedAccount, setSelectedAccount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchMetaAccounts();
      setSelectedAccount('');
    }
  }, [isOpen, fetchMetaAccounts]);

  // Auto-select single options when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      // Auto-select if there's only one account
      if (accounts.length === 1) {
        const singleAccount = accounts[0];
        if (singleAccount.status === 'ACTIVE' || singleAccount.status === 'UNSETTLED') {
          setSelectedAccount(singleAccount.account_id);
        }
      }
    }
  }, [accounts, selectedAccount]);

  const handleAccountSelect = (account) => {
    setSelectedAccount(account.account_id);
  };

  const handleConfirm = async () => {
    if (!selectedAccount) {
      toast.error('Por favor selecciona una cuenta de Meta Ads');
      return;
    }

    const activeBrandId = auth.user?.active_brand_id;

    setIsSubmitting(true);
    try {
      await updateMetaConnectionData({
        accountId: selectedAccount,
      });
      toast.success('Cuenta de Meta Ads configurada exitosamente');
      onClose();

      // If the brand already has Meta objetivos (e.g. backend disconnect/reconnect
      // case), skip the mandatory setup modal so the user lands back on their existing
      // objetivos and optimization suggestions.
      let skipSetupModal = false;
      if (activeBrandId != null) {
        try {
          const objetivos = await queryClient.fetchQuery({
            queryKey: objetivosQueryKeys.list(String(activeBrandId)),
            queryFn: () => fetchObjetivosForBrand(String(activeBrandId)),
            staleTime: 2 * 60 * 1000,
          });
          skipSetupModal = hasPlatformObjetivos(objetivos, 'META');
        } catch (err) {
          console.error('Error checking objetivos before opening setup modal:', err);
          // Fail open: keep current behavior so onboarding never silently stalls.
        }
      }

      if (!skipSetupModal) {
        openCreateObjectivesSetupModal({
          platform: 'META',
          mandatoryUntilGenerated: true,
        });
      }
    } catch (error) {
      console.error('Error setting up Meta Ads account:', error);
      toast.error('Error al configurar la cuenta de Meta Ads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedAccountData = accounts.find(acc => acc.account_id === selectedAccount);
  const isSelectedAccountValid = selectedAccount && selectedAccountData && 
    (selectedAccountData.status === 'ACTIVE' || selectedAccountData.status === 'UNSETTLED');
  const isFormValid = isSelectedAccountValid;

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={onClose}
      {...NON_DISMISSIBLE_MODAL_PROPS}
      titleSlot={
        <>
          <MetaIcon width={22} height={22} />
          <span className="ml-2 text-base font-semibold text-white">Elegí una Cuenta de Meta Ads</span>
        </>
      }
      description="Seleccioná la cuenta de Meta Ads (ad account) para conectar"
      onSubmit={handleConfirm}
      onClose={onClose}
      isSubmitting={isSubmitting}
      submitDisabled={!isFormValid}
      showLogo={true}
      width="520px"
      contentPadding="1.5rem 2rem 2rem 2rem"
      showSubmitButton={true}
    >
      {loadingAccounts ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Cargando cuentas...</p>
        </div>
      ) : metaAccountsPermissionError ? (
        <div className="py-2">
          <MetaPermissionInlineNotice
            variant="light"
            message={metaAccountsPermissionError.message}
            missingPermissions={metaAccountsPermissionError.missingPermissions || []}
            onReconnect={() => startMetaOAuth(true)}
          />
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">No se encontraron cuentas de Meta Ads</p>
        </div>
      ) : (
        <div className="space-y-6">
          <MetaDropdowns
            accounts={accounts}
            selectedAccountId={selectedAccount}
            onAccountSelect={handleAccountSelect}
            disabled={isSubmitting}
            showOnlyAccount={true}
          />
        </div>
      )}
    </GenericModal>
  );
};

export default ConnectMetaAdsAccountModal;
